/**
 * 循环节点 —— 纯逻辑。
 *
 * 语义就是 `map`：拆开列表的每一项，跑一次子图，把每次产出的值收集成 list。
 * 但因为可以有多路输出，它同时也能算「价格列表 + 数量列表」这种并行的东西，
 * 之后用一个函数节点对整条列表求和就得到合计。
 *
 * ## 迭代引擎把「跑一次」**注入**进来
 *
 * `runLoop` 自己不认识图、不认识节点 —— 它只负责：
 *   · 拆列表、算步进、数轮次
 *   · 每一轮调一次 `runOnce(item, index, count)`
 *   · 把每一槽的产出按开关收集起来
 *
 * 所以它能脱离求值层单测（见 verify-loop.ts）。真正的「跑子图」在求值器那边。
 *
 * ## 输出槽：行级「跳过空值」
 *
 * 每一路输出各自决定：这一轮它没值时，是**跳过**（得到真实的 list）
 * 还是**记一个空位**（保持各 list 长度一致、能按行对应）。
 *
 * 默认**跳过** —— 要的是真实数据；想对齐就把那一槽的开关关掉。
 * 注意这是行级的，所以各 list 长度**可以不同**，那是刻意的。
 */
import { isEmpty, toNumber } from './listOps'
import type {
  GraphNodeModel,
  InputRow,
  NodeRow,
  OutputRow,
  RepeaterRow,
  SubGraph,
  SubGraphEdge,
} from '../types/node'
import type { SocketType } from '../types/socket'
import { lastConnectedFromEdges } from './groupInterface'

/** 子图里两个接口节点的固定 id */
export const LOOP_IN_NODE = '__loop_in'
export const LOOP_OUT_NODE = '__loop_out'

/** 循环起始节点注入的三个值 —— 它们是**机制给**的，不是外部连的 */
export const LOOP_ITEM = 'item'
export const LOOP_INDEX = 'index'
export const LOOP_COUNT = 'count'

/**
 * 默认几路输出 —— **0**。
 *
 * ⚠️ 这里原来是 2。那 2 路会让**刚放下来、一根线都没接**的循环外面
 * 直接出现 2 个结果端口，而里面一个都没接 —— 用户的原话：
 * 「没连线就有两个结果，两个空的变参行，这是变参行语义问题吧」。
 *
 * 按变参行的语义：**数量由接线长出，没接就是 0**。
 * 所以默认 0 路，用户在里面接一根，外面才出现 `out0`。
 */
export const DEFAULT_LOOP_SLOTS = 0

/**
 * 步进：不是正数就按 1 算。
 *
 * 不做「负步进倒着走」—— 模板里没有这个需求，
 * 而负数会让 `index < count` 这个循环条件永远成立（死循环）。
 */
export function normalizeStep(value: unknown): number {
  const n = toNumber(value)
  if (n === null || n < 1) return 1
  return Math.trunc(n)
}

/**
 * 跑一次循环。
 *
 * `skipEmpty[slot] !== false` 就是「跳过空值」开着（默认开）。
 */
export function runLoop(options: {
  list: unknown
  step?: unknown
  slots: number
  /** 每一槽的「跳过空值」开关，缺省视为开 */
  skipEmpty?: readonly boolean[]
  /** 跑一轮：返回每一槽的产出 */
  runOnce(item: unknown, index: number, count: number): unknown[]
}): unknown[][] {
  const slots = Math.max(1, Math.trunc(options.slots))
  const buckets: unknown[][] = Array.from({ length: slots }, () => [])

  // 不是数组就当空列表 —— 图里的值随时可能是 undefined，不该把整张图带崩
  const list = Array.isArray(options.list) ? options.list : []
  if (list.length === 0) return buckets

  const step = normalizeStep(options.step)
  const count = list.length

  for (let index = 0; index < count; index += step) {
    const produced = options.runOnce(list[index], index, count)
    for (let slot = 0; slot < slots; slot += 1) {
      const value = produced[slot]
      const skip = options.skipEmpty?.[slot] !== false
      if (skip && isEmpty(value)) continue
      buckets[slot]?.push(value)
    }
  }

  return buckets
}

/* ══════════════════════════════════════════════════════════════
   接口
   ══════════════════════════════════════════════════════════════ */

/** 循环外部可以额外喂进来的参数（除了列表 / 步进） */
export const LOOP_PARAMS = 'params'

/**
 * 循环起始节点：把「当前这一轮」的值送给子图内部。
 *
 * `item` / `index` / `count` 是机制给的；下面那些**参数**是外面
 * （循环节点上的「参数」变参行）喂进来的 —— 外面加了几个，
 * 这里就展开成几个**输出行**，直接接给里面要用的地方。
 *
 * 行 id 用 `params.{i}`，跟外面变参行的 handle 一一对应。
 */
export function loopStartRows(
  paramCount = 1,
  /**
   * 每一路参数**实际确认出来的类型**（外面连上之后才知道）。
   * 传进来，里面接错类型才会被拦住。
   */
  types?: readonly (SocketType | undefined)[],
): NodeRow[] {
  const out = (id: string, label: string, type: SocketType): OutputRow => ({
    kind: 'output',
    id,
    label,
    socket: { id, type },
  })

  /*
   * ★ 参数那一段是**一条输出方向的变参行**，和三行固定输出混在同一个节点里
   * —— 这正是「组自己定义哪些是固定的、哪些是活的」那个要求的样子：
   * `item` / `index` / `count` 是**固定**的（循环跑起来一定有这三样），
   * 参数是**活的**（用户想带几路就几路，在＋/✕里增删）。
   *
   * ⚠️ `min` 就是声明路数，**不留空槽**：输出方向的空槽会在里面凭空
   * 长出一路没接过线的参数（这个坑踩过两次，见上面两处注释）。
   * ⚠️ handle 不变：`id` 还是 `params`、默认模板 `${id}.{i}`
   * → 实例句柄还是 `params.0` / `params.1`（和静态行的 id 一样）。
   */
  return [
    out(LOOP_ITEM, '当前项', 'any'),
    out(LOOP_INDEX, '序号', 'number'),
    out(LOOP_COUNT, '总数', 'number'),
    {
      kind: 'repeater',
      id: LOOP_PARAMS,
      label: '参数',
      socketType: 'any',
      side: 'output',
      min: Math.max(0, paramCount),
      max: 16,
      /*
       * ★★ **末尾留一个空槽** —— 那就是「从里面接一根线长一路参数」的入口，
       * 和组的输入节点（`groupInterface.ts` 里的 `in` 行）**同一条规矩**。
       *
       * ⚠️ 原来这里没有这一项，后果是用户报的那个现象：
       * 「循环节点的输入节点**没有变参行输出端口可以链接**」——
       * `paramCount` 为 0 时渲染 0 个端口，一个都点不到；
       * 声明了几路时渲染几个，**接完不长**（没有那个空槽）。
       *
       * ⚠️ 它是**渲染**用的那个槽，**不是**接口数量 ——
       * 外面 `params.{i}` 有几个由 `innerParamCount` 从**边**推（它不吃 `min`）。
       */
      freeSlot: true,
      instanceTypes: types ? [...types] : undefined,
    },
  ]
}

/** 把内部起始节点的参数行**对齐**到外面声明了几个 */
/**
 * 循环外面的**参数端口行**（静态输入行，id 形如 `params.0`）。
 *
 * ⚠️ 老文档里这一侧是变参行，所以两种形状都要认（见 `loopParamRows` 的调用方）。
 */
export function loopParamRows(rows: readonly NodeRow[]): InputRow[] {
  const prefix = `${LOOP_PARAMS}.`
  return rows.filter((r): r is InputRow => r.kind === 'input' && r.socket.id.startsWith(prefix))
}

/** **内部起始节点声明了几路参数**（接口的基准数量）—— 理由同 `innerInterfaceCount` */
export function innerParamCount(graph: SubGraph): number {
  const node = graph.nodes.find((n) => n.id === LOOP_IN_NODE)
  if (!node) return 0
  /* 新版形状：一条输出变参行 */
  const repeater = node.rows.find(
    (r): r is RepeaterRow => r.kind === 'repeater' && r.id === LOOP_PARAMS,
  )
  if (repeater) {
    /*
     * ★ 从**边**推：里面接了几路就是几路（和组的 `groupInterfaceOf` 对称）。
     *
     * ⚠️ **不能读 `repeaterCount(repeater)`** —— 那是**渲染**件数，
     * 看的是 `row.connected` 这个**视图状态**，而子图里的行没有那份状态，
     * 于是它恒等于 `min`（表现就是「参数数量固定」）。
     * 组那侧为这件事修过一轮，这里是同一个坑的第二次。
     *
     * 起始节点是**源**（`params.{i} → 里面的节点`），所以数 source 侧；
     * target 侧一并算上是为了兼容老形状（静态参数行）。
     */
    const outbound = lastConnectedFromEdges(graph.edges, LOOP_IN_NODE, repeater, 'source')
    const inbound = lastConnectedFromEdges(graph.edges, LOOP_IN_NODE, repeater, 'target')
    const wired = outbound < 0 && inbound < 0 ? 0 : Math.max(outbound, inbound) + 1
    if (wired > 0) return wired
    /*
     * ⚠️ 一条边都没有时**留给老形状的兜底**（下面那行）——
     * 不能在这里直接 `return 0`，否则老文档的静态参数行会被读成 0 路。
     */
  }
  /* 老文档：静态参数行 `params.{i}` */
  return node.rows.filter(
    (r) => r.kind === 'output' && r.id.startsWith(`${LOOP_PARAMS}.`),
  ).length
}

export function syncStartNode(
  graph: SubGraph,
  paramCount: number,
  types?: readonly (SocketType | undefined)[],
): void {
  const node = graph.nodes.find((n) => n.id === LOOP_IN_NODE)
  if (!node) return

  /* ⚠️ 就着已有的参数变参行改（别把用户起的实例名冲掉），理由同 `syncInputNode` */
  const existing = node.rows.find(
    (r): r is RepeaterRow => r.kind === 'repeater' && r.id === LOOP_PARAMS,
  )
  const fresh = loopStartRows(paramCount, types).find(
    (r): r is RepeaterRow => r.kind === 'repeater' && r.id === LOOP_PARAMS,
  )
  if (!existing || !fresh) {
    node.rows = loopStartRows(paramCount, types)
    return
  }
  existing.min = fresh.min
  existing.instanceTypes = fresh.instanceTypes
  if (existing.names && existing.names.length > paramCount) {
    existing.names = existing.names.slice(0, Math.max(0, paramCount))
  }
}

/**
 * 循环输出节点：一路输出一个变参实例。
 *
 * 每一轮往第 k 个实例塞一个值，最后每个实例各自成一条 list。
 * 形状上用**变参行**（而不是 N 个固定行）—— 它天然就是
 * 「一组同类型的输入，数量可增」，正好对上「几路输出」。
 */
/**
 * 结果累记节点的 rows：一个输入变参行。
 *
 * ⚠️ `min` **恒为 1**，不跟着槽数走。
 *
 * 之前把「当时有几路」烘进 `min`，于是删光连线之后里面**还留着那几行** ——
 * `min` 是「至少渲染几行」，而结果的数量应该完全由「接了几路」决定。
 * 烘死的话它再也缩不回去（真实反馈过：删完线还剩两个结果）。
 */
export function loopOutputRows(): NodeRow[] {
  return [
    {
      kind: 'repeater',
      id: 'results',
      label: '结果',
      socketType: 'any',
      /*
       * ⚠️ `min` **不是「声明了几路结果」** —— 它只是「渲染出几个空槽好让人接线」。
       *
       * ```text
       *   1  →  渲染 2 个槽：out.0 可接，out.1 是那个待接的空位（freeSlot）
       * ```
       *
       * 我曾经在这里写过 `min: Math.max(1, slots)`（把外面的
       * `DEFAULT_LOOP_SLOTS` 当成「声明了 2 路结果」）—— **那是错的**，
       * 而且错得很隐蔽：治好了「进去看不见可接的槽」，却造出了
       * 「**一根线都没接，外面就有 2 个结果**」。用户的原话：
       * 「没连线就有两个结果，两个空的变参行，这是变参行语义问题吧」——
       * **是语义问题，而且是我用错了 `min`。**
       *
       * 正确的语义（和组的输出节点一致）：**数量只由里面接线长出**，
       * 没接就是 0 路。`min: 1` 加 `freeSlot` 合起来的意思是
       * 「给你留一个空位，接一根就长一路」。
       */
      min: 1,
      max: 16,
      /* ⚠️ 同组的输出节点：末尾留一个空槽才接得上第一路 */
      freeSlot: true,
    },
  ]
}

/**
 * 循环节点自己的 rows（外面看到的接口）。
 *
 * `列表` / `步进` 是固定输入；输出按槽数生成，
 * 每一行带 `skipEmpty` —— 见文件头那段说明。
 */
export function loopExternalRows(
  slots: number,
  /**
   * 外面出现几个参数端口。
   *
   * ⚠️ **默认 0**（原来是 1）。
   *
   * 参数数量和结果数量是**同一条规矩**：**由里面接了几路决定，没接就是 0**。
   * 写死 1 的后果和结果那一侧一模一样 —— 用户报的：
   * 「外部一个，里边一个空的，**进去出来就没了**」：
   * 外面先凭空有 1 路，一进出就被 `innerParamCount`（只看边）削成 0。
   *
   * ⚠️ 那「一路」不用靠默认值给：内层参数行 `min: 1` + `freeSlot`
   * 本来就会渲染出一个**可接的空槽**，用户接一根外面就长出 `params.0`。
   */
  params = 0,
  /**
   * 每一路结果**在内部确认出来的类型**。
   *
   * 循环的产物是**列表**，所以这里填的是 `itemType` ——
   * 里面接的是 `text`，外面就是 `list<text>`，接错类型会被拦住。
   */
  itemTypes?: readonly (SocketType | undefined)[],
  /**
   * ★ 每一路参数**用户起的名字**（内层起始节点上的 `names`）。
   *
   * ⚠️ 不传的话，里面把「参数 1」改成「当前文件」，外面还是「参数 1」——
   * 而外面那个名字正是接线时唯一能看到的提示。
   */
  paramNames?: readonly (string | undefined)[],
  /** ★ 每一路结果用户起的名字（内层结果累记节点上的 `names`） */
  resultNames?: readonly (string | undefined)[],
): NodeRow[] {
  const inputs: InputRow[] = [
    {
      kind: 'input',
      id: 'list',
      label: '列表',
      socket: { id: 'list', type: 'list' },
    },
    {
      kind: 'input',
      id: 'step',
      label: '步进',
      socket: { id: 'step', type: 'number' },
      widget: { id: 'step', kind: 'number', value: 1, min: 1, precision: 0 },
    },
  ]

  /*
   * 额外的输入参数 —— 每一轮原样传给内部的「循环起始节点」。
   * 「循环里要乘一个系数」这类需求靠它。
   *
   * ★ 和组一样：**静态行**，不是变参行。变参行的空槽意味着
   * 「外面连一根线就能加一路参数」，那等于让外面改内部结构。
   * ⚠️ handle 不变：id 还是 `params.0` / `params.1`。
   */
  const paramRows: InputRow[] = Array.from(
    { length: Math.max(0, params) },
    (_, index): InputRow => {
      const id = `${LOOP_PARAMS}.${index}`
      return {
        kind: 'input',
        id,
        /* 用户起的名字优先；没有才回退「参数 N」 */
        label: paramNames?.[index] || `参数 ${index + 1}`,
        socket: { id, type: 'any' },
        /* 接口行不带内联控件：值只能从外面接进来 */
      }
    },
  )

  const outputs: OutputRow[] = Array.from({ length: Math.max(0, slots) }, (_, index) => ({
    kind: 'output',
    id: `out${index}`,
    /* 同上：用户起的名字优先 */
    label: resultNames?.[index] || `结果 ${index + 1}`,
    socket: {
      id: `out${index}`,
      type: 'list' as SocketType,
      ...(itemTypes?.[index] ? { itemType: itemTypes[index] } : {}),
    },
    // 默认跳过空值：要的是真实数据；想按行对齐就把这一槽关掉
    skipEmpty: true,
  }))

  return [...inputs, ...paramRows, ...outputs]
}

/** 造出循环内部那两个固定节点 */
/**
 * ⚠️ **没有 `slots` 参数了**（原来有）。它曾经被当成「外面声明了几路结果」
 * 传给结果行当 `min` —— 那个用法是错的（见 `loopOutputRows` 的注释）。
 * 结果数量**只由里面的边决定**，所以内层不需要知道外面的什么「槽数」。
 */
export function loopInnerNodes(params = 0): GraphNodeModel[] {
  return [
    {
      id: LOOP_IN_NODE,
      type: 'loop.start',
      title: '循环起始',
      category: 'utility',
      rows: loopStartRows(params),
    },
    {
      id: LOOP_OUT_NODE,
      type: 'loop.output',
      title: '结果累记',
      category: 'utility',
      rows: loopOutputRows(),
    },
  ]
}

/**
 * 从循环节点自己的 rows 里读出「每一槽跳不跳过空值」。
 *
 * 缺省视为**跳过**（`!== false`），跟 runLoop 保持一致 ——
 * 两处各写一遍的话，界面上开关是开的、求值却在补空位，很难查。
 */
export function skipEmptyFlags(rows: readonly NodeRow[]): boolean[] {
  return rows
    .filter((row): row is OutputRow => row.kind === 'output')
    .map((row) => row.skipEmpty !== false)
}

/**
 * 某一行上**已被边连接的实例数**。
 *
 * ⚠️ 不再钳到 `min` —— `min` 是渲染需要的（末尾留个槽好接线），
 * 而接口的真实数量就是「连了几个」：一个都没连 → 0 路输出。
 */
function wiredCount(
  row: RepeaterRow | undefined,
  edges: readonly SubGraphEdge[],
  nodeId: string,
): number {
  if (!row) return 1
  const min = Math.max(1, row.min ?? 1)
  let last = -1
  // handle 模板默认是 `${id}.${i}`
  const pattern = row.handlePattern ?? `${row.id}.{i}`
  for (const edge of edges) {
    if (edge.target !== nodeId) continue
    for (let i = 0; i < min + 64; i += 1) {
      if (pattern.replace('{i}', String(i)) === (edge.targetHandle ?? '')) {
        if (i > last) last = i
        break
      }
    }
  }
  /*
   * ⚠️ 用 `last + 1` 而不是 `repeaterCountFor(row, last)`。
   *
   * 后者会**多给一个空槽**（渲染上要留一行好继续连），但外部接口该镜像的
   * 是「实际接了几个」，不是「画了几行」—— 否则里面接 1 路，外面会冒出 2 路。
   *
   * 也**不钳到 `min`** —— 一个都没接就是 0 路。
   */
  /*
   * ★ **只看边**：里面接了几路，外面就是几路。没接就是 0。
   *
   * ⚠️ 我曾经在这里取过 `Math.max(min, last + 1)`（想保住「外面声明过的那几路」）
   * —— 那是**配合一个错误前提**打的补丁：当时把 `min` 当成了「声明了几路」。
   * `min` 只表示「渲染几个空槽」，不是数量声明，所以这里不该读它。
   * （`min` 变量因此只用来算扫描上界，见上面那圈循环。）
   */
  return last + 1
}

/**
 * 从子图里那两个固定节点**反推**循环的外部 rows。
 *
 * ★ 「从里面改接口」靠它：给循环起始节点加一个参数、或者给结果累记节点
 * 多接一路，退出来（或随时）循环对外声明的接口就该跟着变。
 */
export function loopRowsFromGraph(
  graph: SubGraph,
  paramCount: number,
  itemTypes?: readonly (SocketType | undefined)[],
): NodeRow[] {
  const out = graph.nodes.find((n) => n.id === LOOP_OUT_NODE)
  const resultRow = out?.rows.find((r): r is RepeaterRow => r.kind === 'repeater')

  /*
   * ⚠️ 参数数量**不由内部反推** —— 它由外面决定（用户在外面加）。
   * 从里面反推的话，外面刚加一个参数、里面还没接，就会被推回 1 个。
   */
  const startRow = graph.nodes
    .find((n) => n.id === LOOP_IN_NODE)
    ?.rows.find((r): r is RepeaterRow => r.kind === 'repeater' && r.id === LOOP_PARAMS)
  return loopExternalRows(
    wiredCount(resultRow, graph.edges, LOOP_OUT_NODE),
    paramCount,
    resultRow?.instanceTypes ?? itemTypes,
    startRow?.names,
    resultRow?.names,
  )
}

/** 循环从菜单拖出来时，子图里该有的两个固定节点 */
/**
 * 从菜单拖出来时的初始子图。
 *
 * ⚠️ 两个数量默认都是 **0** —— 刚拖出来的循环什么都没连，
 * 内部就不该有参数行 / 结果行。填 1 会凭空长出接口。
 */
export function emptyLoopGraph(params = 0): SubGraph {
  return {
    nodes: loopInnerNodes(params),
    edges: [],
    positions: {
      [LOOP_IN_NODE]: { x: 0, y: 0 },
      [LOOP_OUT_NODE]: { x: 440, y: 0 },
    },
  }
}

/** 循环节点当前声明了几路输出 */
export function slotCountOf(rows: readonly NodeRow[]): number {
  return rows.filter((row) => row.kind === 'output').length
}
